
import React, { useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { X, Upload, Loader2, AlertCircle } from 'lucide-react';
import { PendingImageUpload } from '@/types/chat';
import { cn } from '@/lib/utils';

interface ImageAttachmentAreaProps {
  pendingImages: PendingImageUpload[];
  uploading: boolean;
  onAddImages: (files: File[]) => void;
  onRemoveImage: (index: number) => void;
  disabled?: boolean;
}

const ImageAttachmentArea: React.FC<ImageAttachmentAreaProps> = ({
  pendingImages,
  uploading,
  onAddImages,
  onRemoveImage,
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onAddImages(files);
    }
    // Reset input value to allow selecting the same files again
    e.target.value = '';
  }, [onAddImages]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      onAddImages(files);
    }
  }, [onAddImages, disabled]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const openFileDialog = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  if (pendingImages.length === 0) {
    return (
      <div className="mb-2">
        <div
          className={cn(
            "border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center transition-colors",
            !disabled && "hover:border-muted-foreground/50 cursor-pointer",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={openFileDialog}
        >
          <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Drop images here or click to select
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Max 3 images, 5MB each
          </p>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div className="mb-2">
      <div className="grid grid-cols-3 gap-2 mb-2">
        {pendingImages.map((image, index) => (
          <div key={index} className="relative group">
            <div className="aspect-square relative rounded-lg overflow-hidden bg-muted">
              <img
                src={image.preview}
                alt={`Upload ${index + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Status overlay */}
              <div className={cn(
                "absolute inset-0 flex items-center justify-center transition-opacity",
                image.uploading ? "bg-black/50" : "bg-black/0 group-hover:bg-black/20"
              )}>
                {image.uploading && (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                )}
                {image.error && (
                  <AlertCircle className="h-6 w-6 text-red-500" />
                )}
              </div>
              
              {/* Remove button */}
              {!image.uploading && (
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => onRemoveImage(index)}
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
              
              {/* Upload progress */}
              {image.uploading && image.progress !== undefined && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
                  <div
                    className="h-full bg-primary transition-all duration-200"
                    style={{ width: `${image.progress}%` }}
                  />
                </div>
              )}
            </div>
            
            {/* Error message */}
            {image.error && (
              <p className="text-xs text-red-500 mt-1 truncate" title={image.error}>
                {image.error}
              </p>
            )}
          </div>
        ))}
        
        {/* Add more images button */}
        {pendingImages.length < 3 && !uploading && (
          <div
            className={cn(
              "aspect-square border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center transition-colors",
              !disabled && "hover:border-muted-foreground/50 cursor-pointer",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            onClick={openFileDialog}
          >
            <Upload className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled}
      />
    </div>
  );
};

export default ImageAttachmentArea;
